import { Component, h, Prop, State, Element } from '@stencil/core';
import { UserService, User } from '../aafp-authentication-wrapper/userService';
import { CreditReportingService, JournalIssueCredit } from '../aafp-cme-quiz-card/creditReportingService';

/**
 * COMPNAME is for displaying a content gate on restricted journal articles. It should be paired with an aafp-modal that displays the actual product offerings.
 */
@Component({
  tag: 'aafp-journal-content-gate',
  shadow: false,
})
export class AafpJournalContentGate {
  /**
   * For the heading
   */
  @Prop() heading: string;
  /**
   * For the description (rich text)
   */
  @Prop() description: string;
  /**
   * For the label of the "login" button
   */
  @Prop() loginButtonLabel: string;
  /**
   * For the redirect url of the "login" button
   */
  @Prop() loginButtonUrl: string;
  /**
   * For the label of the "purchase" button
   */
  @Prop() purchaseButtonLabel: string;
  /**
   * An id string that should match the action-id of a pricing modal (aafp-modal) on the same page
   */
  @Prop() purchaseModalId!: string;
  /**
   * Salesforce authentication endpoint required for initializing the user service (for retrieving an AAFPID)
   */
  @Prop() authEndpoint!: string;
  /**
   * Endpoint for retrieving information about the number of CME credits
   */
  @Prop() creditReportingEndpoint!: string;
  /**
   * Identifier for the journal, volume and issue / edition number (e.g. AFP1034)
   */
  @Prop() issueIdentifier!: string;

  @State() journalIssueCredit: JournalIssueCredit | null;

  @State() isLoggedIn: boolean = false;

  @Element() element: HTMLElement;

  private userService: any;
  private creditReportingService: CreditReportingService;

  componentWillLoad() {
    this.userService = new UserService(this.authEndpoint);
    this.creditReportingService = new CreditReportingService(this.creditReportingEndpoint);

    this.renderJournalIssueCredit();
  }

  private async renderJournalIssueCredit() {
    const purchaseModal: any = document.getElementById(this.purchaseModalId);

    if (purchaseModal) {
      await this.getJournalIssueCredit();

      [...purchaseModal.querySelectorAll('[data-cme-credits]')].forEach((placeholderEl) => {
        const cmeCredits: number | undefined = this.journalIssueCredit?.PrescribedCredit;

        if (cmeCredits) {
          placeholderEl.textContent = cmeCredits;
        }
      });
    }
  }

  private async getJournalIssueCredit() {
    let issueIdentifier = this.issueIdentifier;
    let aafpId;

    const userData: User | null = await this.userService.getUserData();

    if (userData) {
      this.isLoggedIn = parseInt(userData.IsLoggedIn) === 1;
      aafpId = userData.AAFPID || '';
    }

    this.journalIssueCredit = await this.creditReportingService.get(issueIdentifier, aafpId);
  }

  private onPurchaseButtonClick = (e: Event) => {
    e.preventDefault();

    const purchaseModal: any = document.querySelector(`[action-id="${this.purchaseModalId}"]`);

    if (purchaseModal) {
      purchaseModal.showModal(this.purchaseModalId);

      if (this.isLoggedIn) {
        // remove login header if user is logged in
        const purchaseHeader = document.querySelector<HTMLElement>('.adl-journal-content-gate-modal__header');
        if (purchaseHeader !== null) {
          purchaseHeader.style.display = 'none';
        }
      }
    }
  };

  render() {
    return (
      <div class="adl-journal-content-gate">
        <div class="adl-journal-content-gate__dialog">
          <div class="adl-body adl-journal-content-gate__text">
            {this.heading && <h2 class="adl-journal-content-gate__heading">{this.heading}</h2>}
            {this.description && (
              <div class="adl-body adl-journal-content-gate__description" innerHTML={this.description} />
            )}
          </div>
          <div class="adl-journal-content-gate__actions">
            {this.loginButtonLabel && this.loginButtonUrl && !this.isLoggedIn && (
              <aafp-cta-button
                label={this.loginButtonLabel}
                isActive={true}
                isSecondary={true}
                isExternalLink={false}
                href={this.loginButtonUrl}
              ></aafp-cta-button>
            )}
            {this.purchaseButtonLabel && (
              <aafp-cta-button
                label={this.purchaseButtonLabel}
                isActive={true}
                isSecondary={true}
                isExternalLink={false}
                href="javascript:void(0);"
                onClick={this.onPurchaseButtonClick}
              ></aafp-cta-button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
